import React, { useEffect, useState } from 'react'
import * as ReactBootstrap from 'react-bootstrap'

import { SupportPackageService } from '../../../services'

interface DeviceSupportPackagesProps {
  // Serial number / IMEI / IMEI2 printed on the device. The backend ORs the
  // search across all three identifiers.
  device_identifier: string
}

interface SupportPackage {
  url?: string
  device_serial_number: string
  imei1?: string | null
  imei2?: string | null
  start_date: string
  end_date: string
  sold_by?: string | null
  sold_on?: string | null
}

const today = (): string => new Date().toISOString().slice(0, 10)

const isActive = (pkg: SupportPackage): boolean =>
  (!pkg.start_date || pkg.start_date <= today()) &&
  (!pkg.end_date || pkg.end_date >= today())

export const DeviceSupportPackages: React.FC<DeviceSupportPackagesProps> = ({
  device_identifier,
}) => {
  const [packages, setPackages] = useState<SupportPackage[] | null>(null)
  const [error, setError] = useState(false)

  useEffect(() => {
    let cancelled = false
    setPackages(null)
    setError(false)
    new SupportPackageService()
      .makeSearchRequest(device_identifier)
      .then((data: any) => {
        if (cancelled) return
        setPackages(Array.isArray(data) ? data : data?.results ?? [])
      })
      .catch(() => {
        if (cancelled) return
        setError(true)
        setPackages([])
      })
    return () => {
      cancelled = true
    }
  }, [device_identifier])

  if (packages === null) {
    return (
      <div className="p-4 text-center text-muted">
        <ReactBootstrap.Spinner animation="border" size="sm" className="me-2" />
        Checking support packages...
      </div>
    )
  }

  if (error) {
    return (
      <div className="p-3 text-danger">
        Could not load support packages for this device.
      </div>
    )
  }

  if (packages.length === 0) {
    return (
      <div className="p-4 text-center text-muted">
        No support packages found for <strong>{device_identifier}</strong>.
      </div>
    )
  }

  return (
    <div className="p-2">
      <ReactBootstrap.Table striped bordered hover responsive size="sm">
        <thead>
          <tr>
            <th>Device serial</th>
            <th>IMEI 1</th>
            <th>IMEI 2</th>
            <th>Coverage</th>
            <th>Status</th>
            <th>Sold by</th>
            <th>Sold on</th>
          </tr>
        </thead>
        <tbody>
          {packages.map((pkg) => (
            <tr key={pkg.url ?? pkg.device_serial_number}>
              <td>{pkg.device_serial_number}</td>
              <td>{pkg.imei1 || '-'}</td>
              <td>{pkg.imei2 || '-'}</td>
              <td>
                {pkg.start_date} &rarr; {pkg.end_date}
              </td>
              <td>
                {isActive(pkg) ? (
                  <ReactBootstrap.Badge bg="success">
                    Active
                  </ReactBootstrap.Badge>
                ) : (
                  <ReactBootstrap.Badge bg="secondary">
                    Expired
                  </ReactBootstrap.Badge>
                )}
              </td>
              <td>{pkg.sold_by || '-'}</td>
              <td>{pkg.sold_on || '-'}</td>
            </tr>
          ))}
        </tbody>
      </ReactBootstrap.Table>
    </div>
  )
}
